import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Lesson } from '../types';
import { ContentEditor, ContentBlock } from '../components/ContentEditor';
import { ChevronLeft, Eye } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../api/client';

export function LessonEditorPage() {
  const { lessonId }              = useParams();
  const navigate                  = useNavigate();
  const [lesson, setLesson]       = useState<Lesson | null>(null);
  const [loading, setLoading]     = useState(true);
  const [saving, setSaving]       = useState(false);

  useEffect(() => {
    if (!lessonId) return;
    api.get(`/api/v1/lessons/${lessonId}`)
      .then(res => setLesson(res.data))
      .catch(() => toast.error('Failed to load lesson'))
      .finally(() => setLoading(false));
  }, [lessonId]);

  const handleSave = async (blocks: ContentBlock[]) => {
    if (!lessonId) return;
    setSaving(true);
    try {
      await api.put(`/api/v1/lessons/${lessonId}/content`, {
        contentBlocks: blocks,
        title: lesson?.title,
      });
      toast.success('Lesson saved! ✅');
    } catch {
      toast.error('Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-8 text-gray-500">
        Loading editor...
      </div>
    );
  }

  if (!lesson) return null;

  return (
    <div className="max-w-3xl mx-auto px-6 py-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-gray-500
                     hover:text-white transition-colors text-sm"
        >
          <ChevronLeft size={16} />
          Back
        </button>
        <button
          onClick={() => navigate(`/lessons/${lessonId}/preview`)}
          className="flex items-center gap-2 text-gray-400
                     hover:text-white transition-colors text-sm"
        >
          <Eye size={16} />
          Preview
        </button>
      </div>

      <div className="mb-6">
        <div className="text-xs text-green-400 font-bold mb-2">
          EDITING LESSON
        </div>
        <h1 className="text-2xl font-black text-white">
          {lesson.title}
        </h1>
        <p className="text-gray-500 text-sm mt-1">
          Click any block to edit · Drag to reorder · + to add new blocks
        </p>
      </div>

      <ContentEditor
        initialBlocks={lesson.contentBlocks as ContentBlock[]}
        onSave={handleSave}
        saving={saving}
      />
    </div>
  );
}